import config from '../config';

const SHEETS_API_KEY = config.GOOGLE_API_KEY;
const SHEETS_API = config.GOOGLE_SHEETS_API;

// Hijri month names for display
const HIJRI_MONTH_NAMES = [
  '', 'Muharram', 'Safar', "Rabi' al-Awwal", "Rabi' al-Thani",
  "Jumada al-Awwal", "Jumada al-Thani", 'Rajab', "Sha'ban", 'Ramadan',
  'Shawwal', "Dhul Qi'dah", "Dhul Hijjah"
];

const HIJRI_MONTH_ABBR = [
  '', 'Muh.', 'Saf.', "Rab.I", "Rab.II",
  "Jum.I", "Jum.II", 'Raj.', "Sha.", 'Ram.',
  'Shaw.', "DhQ.", "DhH."
];

// In-memory caches (per session)
let occasionsCache = null;
let calendarCache = null;
let calendarCacheCenterId = null;

/**
 * Fetch master occasions from the shared Google Sheet
 * @param {string} sheetId - Master occasions sheet ID
 * @returns {Array} Array of occasion objects
 */
export async function fetchOccasions(sheetId) {
  if (occasionsCache) return occasionsCache;
  if (!sheetId) return [];

  try {
    // Try known tab names — CSV imports use filename, manual sheets may use Sheet1
    const tabNames = ['islamic_occasions_master', 'Sheet1', 'Occasions'];
    let response = null;
    for (const tab of tabNames) {
      const range = `${tab}!A1:L200`;
      const url = `${SHEETS_API}/${sheetId}/values/${encodeURIComponent(range)}?key=${SHEETS_API_KEY}`;
      response = await fetch(url);
      if (response.ok) {
        console.log(`[IslamicOccasions] Found occasions data in tab: ${tab}`);
        break;
      }
    }
    if (!response || !response.ok) {
      console.warn('[IslamicOccasions] Failed to fetch occasions sheet from any tab');
      return [];
    }

    const data = await response.json();
    const rows = data.values || [];
    if (rows.length < 2) return [];

    const headers = rows[0].map(h => h.toLowerCase().trim());
    const occasions = [];

    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      const get = (col) => {
        const idx = headers.indexOf(col);
        return idx >= 0 && row[idx] ? row[idx].trim() : '';
      };

      // Skip non-visible occasions
      const isVisible = get('is_visible');
      if (isVisible.toUpperCase() === 'FALSE') continue;

      const hijriMonth = parseInt(get('hijri_month'), 10);
      const hijriDay = parseInt(get('hijri_day'), 10);
      if (!hijriMonth || !hijriDay) continue;

      occasions.push({
        hijriMonth,
        hijriDay,
        nameEn: get('occasion_name_en'),
        nameAr: get('occasion_name_ar'),
        type: get('occasion_type') || 'significant', // joyous, mourning, significant
        significance: get('significance'),
        amaalUrlPrimary: get('amaal_url_primary'),
        amaalUrlSecondary: get('amaal_url_secondary'),
        amaalSummary: get('amaal_summary'),
        isHoliday: get('is_holiday').toUpperCase() === 'TRUE',
        durationDays: parseInt(get('duration_days'), 10) || 1,
      });
    }

    occasionsCache = occasions;
    console.log(`[IslamicOccasions] Loaded ${occasions.length} visible occasions`);
    return occasions;
  } catch (err) {
    console.warn('[IslamicOccasions] Error fetching occasions:', err.message);
    return [];
  }
}

/**
 * Fetch per-center Hijri calendar from the center's Google Sheet (Islamic Calendar tab)
 * @param {string} sheetId - Center's Google Sheet ID (same as google_sheet_id)
 * @param {string} centerId - Center ID for cache invalidation
 * @returns {Array} Array of { hijriYear, hijriMonth, monthNameEn, gregorianStartDate } sorted by date
 */
export async function fetchCenterCalendar(sheetId, centerId) {
  if (calendarCache && calendarCacheCenterId === centerId) return calendarCache;
  if (!sheetId) return [];

  try {
    const range = 'Islamic Calendar!A1:F100';
    const url = `${SHEETS_API}/${sheetId}/values/${range}?key=${SHEETS_API_KEY}`;
    const response = await fetch(url);
    if (!response.ok) {
      console.warn('[IslamicOccasions] No Islamic Calendar tab found or fetch failed:', response.status);
      return [];
    }

    const data = await response.json();
    const rows = data.values || [];
    if (rows.length < 2) return [];

    const headers = rows[0].map(h => h.toLowerCase().replace(/\s+/g, '_').trim());
    const calendar = [];

    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      const get = (col) => {
        const idx = headers.indexOf(col);
        return idx >= 0 && row[idx] ? row[idx].trim() : '';
      };

      const hijriYear = parseInt(get('hijri_year'), 10);
      const hijriMonth = parseInt(get('hijri_month'), 10);
      const gregorianStartStr = get('gregorian_start_date');
      if (!hijriYear || !hijriMonth || !gregorianStartStr) continue;

      // Parse date string (YYYY-MM-DD or any format Date can parse)
      const gregorianStartDate = new Date(gregorianStartStr + 'T00:00:00');
      if (isNaN(gregorianStartDate.getTime())) continue;

      calendar.push({
        hijriYear,
        hijriMonth,
        monthNameEn: get('month_name_en') || HIJRI_MONTH_NAMES[hijriMonth] || '',
        gregorianStartDate,
        confirmed: get('confirmed').toUpperCase() === 'TRUE',
      });
    }

    // Sort by Gregorian start date ascending
    calendar.sort((a, b) => a.gregorianStartDate - b.gregorianStartDate);

    calendarCache = calendar;
    calendarCacheCenterId = centerId;
    console.log(`[IslamicOccasions] Loaded ${calendar.length} calendar entries for center`);
    return calendar;
  } catch (err) {
    console.warn('[IslamicOccasions] Error fetching center calendar:', err.message);
    return [];
  }
}

/**
 * Convert a Gregorian date to Hijri using the per-center calendar (simple range lookup)
 * @param {Date|string} targetDate - Gregorian date
 * @param {Array} centerCalendar - From fetchCenterCalendar()
 * @returns {{ year: number, month: number, day: number, monthName: string, monthAbbr: string } | null}
 */
export function gregorianToHijri(targetDate, centerCalendar) {
  if (!centerCalendar || centerCalendar.length === 0) return null;

  const target = typeof targetDate === 'string'
    ? new Date(targetDate + (targetDate.includes('T') ? '' : 'T00:00:00'))
    : new Date(targetDate);

  // Walk backwards through calendar to find the most recent month start ≤ target
  for (let i = centerCalendar.length - 1; i >= 0; i--) {
    const month = centerCalendar[i];
    if (target >= month.gregorianStartDate) {
      const diffMs = target.getTime() - month.gregorianStartDate.getTime();
      const dayOffset = Math.floor(diffMs / (24 * 60 * 60 * 1000));
      const hijriDay = dayOffset + 1;

      // Sanity: Hijri months are 29-30 days. If day > 30, we're missing a month entry
      if (hijriDay > 30) return null;

      return {
        year: month.hijriYear,
        month: month.hijriMonth,
        day: hijriDay,
        monthName: HIJRI_MONTH_NAMES[month.hijriMonth] || '',
        monthAbbr: HIJRI_MONTH_ABBR[month.hijriMonth] || '',
      };
    }
  }
  return null;
}

/**
 * Get the Hijri date string for display
 * @param {object} hijri - From gregorianToHijri()
 * @param {'full'|'short'|'day'} format
 * @returns {string}
 */
export function formatHijriDate(hijri, format = 'full') {
  if (!hijri) return '';
  if (format === 'day') return `${hijri.day}`;
  if (format === 'short') {
    // For day 1, show full month name to mark the boundary
    if (hijri.day === 1) return `1 ${hijri.monthName}`;
    return `${hijri.day} ${hijri.monthAbbr}`;
  }
  return `${hijri.day} ${hijri.monthName} ${hijri.year}`;
}

/**
 * Find the occasion(s) for a given Hijri date
 * @param {number} hijriMonth
 * @param {number} hijriDay
 * @param {Array} occasions - From fetchOccasions()
 * @returns {Array} matching occasions (usually 0 or 1, sometimes 2+ for same day)
 */
export function getOccasionsForDate(hijriMonth, hijriDay, occasions) {
  if (!occasions || !hijriMonth || !hijriDay) return [];

  return occasions.filter(o => {
    // Exact match
    if (o.hijriMonth === hijriMonth && o.hijriDay === hijriDay) return true;
    // Multi-day occasions: check if hijriDay falls within duration
    if (o.durationDays > 1 && o.hijriMonth === hijriMonth) {
      return hijriDay >= o.hijriDay && hijriDay < o.hijriDay + o.durationDays;
    }
    return false;
  });
}

/**
 * Get today's occasion (if any)
 * @param {Array} centerCalendar
 * @param {Array} occasions
 * @returns {{ hijri: object, occasions: Array } | null}
 */
export function getTodaysOccasion(centerCalendar, occasions) {
  const today = new Date();
  const hijri = gregorianToHijri(today, centerCalendar);
  if (!hijri) return null;

  const todayOccasions = getOccasionsForDate(hijri.month, hijri.day, occasions);
  return { hijri, occasions: todayOccasions };
}

/**
 * Get occasion data for every day in a Gregorian month (for calendar overlay)
 * @param {number} gYear - Gregorian year
 * @param {number} gMonth - Gregorian month (1-12)
 * @param {Array} centerCalendar
 * @param {Array} occasions
 * @returns {Map<string, { hijri: object, occasions: Array }>} key = 'YYYY-MM-DD'
 */
export function getMonthOccasionMap(gYear, gMonth, centerCalendar, occasions) {
  const map = new Map();
  if (!centerCalendar || centerCalendar.length === 0) return map;

  const daysInMonth = new Date(gYear, gMonth, 0).getDate();

  // Determine the current Islamic month boundary for smart display.
  // We only show Hijri dates for the current Islamic month and past months.
  // Future months (beyond the current Islamic month) are unconfirmed.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayHijri = gregorianToHijri(today, centerCalendar);

  // Find the current Islamic month's entry to determine its confirmed status
  // and the next month's entry to find where it ends
  let currentMonthEntry = null;
  let nextMonthEntry = null;
  if (todayHijri) {
    for (let i = 0; i < centerCalendar.length; i++) {
      const entry = centerCalendar[i];
      if (entry.hijriYear === todayHijri.year && entry.hijriMonth === todayHijri.month) {
        currentMonthEntry = entry;
        if (i + 1 < centerCalendar.length) {
          nextMonthEntry = centerCalendar[i + 1];
        }
        break;
      }
    }
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${gYear}-${String(gMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const hijri = gregorianToHijri(dateStr, centerCalendar);
    if (hijri) {
      const dayOccasions = getOccasionsForDate(hijri.month, hijri.day, occasions);

      // Determine if this date is beyond the current Islamic month (unconfirmed future)
      let isFutureUnconfirmed = false;
      if (todayHijri) {
        const dateObj = new Date(dateStr + 'T00:00:00');
        if (dateObj > today) {
          // Future Gregorian date — check if it's in a future Islamic month
          if (hijri.year > todayHijri.year ||
              (hijri.year === todayHijri.year && hijri.month > todayHijri.month)) {
            // Check if the next month entry is confirmed
            const futureEntry = centerCalendar.find(
              e => e.hijriYear === hijri.year && e.hijriMonth === hijri.month
            );
            if (!futureEntry || !futureEntry.confirmed) {
              isFutureUnconfirmed = true;
            }
          }
        }
      }

      // Check if this is the last day before an unconfirmed next month
      // Shows moon emoji to signal "next month depends on moon sighting"
      let isLastDayOfMonth = false;
      if (nextMonthEntry && !nextMonthEntry.confirmed &&
          hijri.year === todayHijri?.year && hijri.month === todayHijri?.month) {
        const dateObj = new Date(dateStr + 'T00:00:00');
        const nextStart = new Date(nextMonthEntry.gregorianStartDate);
        nextStart.setHours(0, 0, 0, 0);
        const tomorrow = new Date(dateObj);
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (tomorrow.getTime() === nextStart.getTime()) {
          isLastDayOfMonth = true;
        }
      }

      map.set(dateStr, { hijri, occasions: dayOccasions, isFutureUnconfirmed, isLastDayOfMonth });
    }
  }

  return map;
}

/**
 * Determine the mood for today based on occasions
 * @param {Array} todayOccasions - Occasions for today
 * @returns {'joyous'|'mourning'|'normal'}
 */
export function getMood(todayOccasions) {
  if (!todayOccasions || todayOccasions.length === 0) return 'normal';
  // Mourning takes precedence (you don't celebrate during mourning)
  if (todayOccasions.some(o => o.type === 'mourning')) return 'mourning';
  if (todayOccasions.some(o => o.type === 'joyous')) return 'joyous';
  return 'normal';
}

/**
 * Clear caches (e.g., when config changes or for testing)
 */
export function clearCaches() {
  occasionsCache = null;
  calendarCache = null;
  calendarCacheCenterId = null;
}

export { HIJRI_MONTH_NAMES, HIJRI_MONTH_ABBR };
